import { VENUE_MANAGEMENT_ENABLED } from "../../config/meetingLocationFeatures";
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { canManageVenues } from "../../config/venueManagementAccess";
import { useSelector, useDispatch } from "react-redux";
import {
  Settings as SettingsIcon,
  User,
  MapPin,
  Bell,
  Mail,
  EyeOff,
  Trash2,
  ChevronRight,
  X,
  AlertTriangle,
  Loader2,
  ArrowLeft,
  LogOut,
} from "lucide-react";
import "./SettingsPage.css";
import appIcon from "../../assets/appIcon.svg";
import userPlaceholder from "../../assets/userPlaceholder.png";
import dogPlaceholder from "../../assets/dogPlaceholder.png";
import TopNavBar from "../../components/TopNavBar";
import MobileTopBar from "../../components/MobileTopBar";
import sadDog from "../../assets/dogLevel5.png";
import apiClient from "../../api/apiClient";

import { logout, setEmailSubscription } from "../../redux/userSlice";
import { useGlobalAlert } from "../../context/AlertContext";

const getFirstName = (fullName) => {
  if (!fullName) return "";
  return fullName.trim().split(" ")[0];
};

const SettingsPage = ({ setFormData }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { profile, emailSubscribed } = useSelector((state) => state.user);
  const { triggerAlert } = useGlobalAlert();

  const [deleteStep, setDeleteStep] = useState("none");
  const [deleteInput, setDeleteInput] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const [locationEnabled, setLocationEnabled] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [isIOSSafariBrowser, setIsIOSSafariBrowser] = useState(false);

  const [isEmailLoading, setIsEmailLoading] = useState(false);

  const isTeamPaws =
    profile?.user_dog_status === 1 || profile?.dogs?.[0]?.team_paws === 1;
  const dog = profile?.dogs?.[0];

  const userFirstName = getFirstName(profile?.user_name) || "User";
  const dogFirstName = getFirstName(dog?.dog_name);

  const displayName =
    isTeamPaws && dogFirstName
      ? `${userFirstName} & ${dogFirstName}`
      : userFirstName;

  let avatarSrc = userPlaceholder;

  if (isTeamPaws) {
    const dogImages = dog?.dog_images || dog?.profile_image;
    const firstDogImg = Array.isArray(dogImages) ? dogImages[0] : dogImages;

    avatarSrc =
      typeof firstDogImg === "string" && firstDogImg.trim() !== ""
        ? firstDogImg
        : dogPlaceholder;
  } else {
    const userImages = profile?.user_profile_image;
    const firstUserImg = Array.isArray(userImages) ? userImages[0] : userImages;

    avatarSrc =
      typeof firstUserImg === "string" && firstUserImg.trim() !== ""
        ? firstUserImg
        : userPlaceholder;
  }

  useEffect(() => {
    if (deleteStep !== "none" && deleteStep !== "success") {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [deleteStep]);

  useEffect(() => {
    let isMounted = true;

    const ua = navigator.userAgent.toLowerCase();
    const isIOS =
      /ipad|iphone|ipod/.test(ua) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const isChrome = /chrome|crios/.test(ua) && !/edge|edg|opr/.test(ua);
    const isSafari = /safari/.test(ua) && !isChrome;
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;

    if (isIOS && isSafari && !isStandalone) {
      setIsIOSSafariBrowser(true);
    }

    const checkPermissions = async () => {
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const locPermStatus = await navigator.permissions.query({
            name: "geolocation",
          });
          if (isMounted) setLocationEnabled(locPermStatus.state === "granted");
          locPermStatus.onchange = () => {
            if (isMounted)
              setLocationEnabled(locPermStatus.state === "granted");
          };
        } catch (e) {
          console.warn("Location permission query not supported");
        }
      }

      if ("Notification" in window) {
        if (isMounted)
          setNotificationsEnabled(Notification.permission === "granted");
        if (navigator.permissions && navigator.permissions.query) {
          try {
            const notifPermStatus = await navigator.permissions.query({
              name: "notifications",
            });
            notifPermStatus.onchange = () => {
              if (isMounted)
                setNotificationsEnabled(notifPermStatus.state === "granted");
            };
          } catch (e) {
            console.warn("Notifications permission query not supported");
          }
        }
      }
    };

    checkPermissions();

    return () => {
      isMounted = false;
    };
  }, []);

  const requestLocation = () => {
    if (!locationEnabled && "geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        () => {
          setLocationEnabled(true);
          window.dispatchEvent(new Event("locationGranted"));
        },
        (error) => {
          console.warn("Location permission denied or unavailable", error);
          setLocationEnabled(false);
        },
        { enableHighAccuracy: true, timeout: 10000 },
      );
    }
  };

  const requestNotifications = async () => {
    if (isIOSSafariBrowser) return;

    if (!notificationsEnabled && "Notification" in window) {
      try {
        const permission = await Notification.requestPermission();
        setNotificationsEnabled(permission === "granted");
      } catch (error) {
        console.warn("Notification permission request failed", error);
      }
    }
  };

  const handleEmailSubscriptionToggle = async () => {
    if (isEmailLoading) return;

    if (!profile?.user_email) {
      triggerAlert("Email address not found", "error");
      return;
    }

    setIsEmailLoading(true);
    const endpoint = emailSubscribed
      ? "/emailSubscribe/unsubscribe"
      : "/emailSubscribe/resubscribe";

    try {
      const res = await apiClient.post(endpoint, { email: profile.user_email });
      if (res.data?.status) {
        dispatch(setEmailSubscription(!emailSubscribed));
        triggerAlert(
          res.data.message ||
            `Email ${emailSubscribed ? "unsubscribed" : "resubscribed"} successfully`,
          "success",
        );
      }
    } catch (err) {
      triggerAlert("Failed to update email preferences", "error");
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/user-profile");
  };

  const executeDeleteProfile = async () => {
    if (!profile?.id) return;
    setIsDeleting(true);
    try {
      await apiClient.delete(`/user/delete-user-byId/${profile.id}`);
      setDeleteStep("success");
    } catch (err) {
      console.error("Failed to delete profile:", err);
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to delete account.";
      triggerAlert(errorMsg, "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.removeItem("sp_onboarding_form");

    if (setFormData) {
      setFormData({
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
        street: "",
        apartment: "",
        city: "",
        state: "",
        zip: "",
        team: "",
        dogName: "",
        dogBreed: "",
        selectedBreed1: "",
        selectedBreed2: "",
        selectedBreed3: "",
        dogGender: "",
        dogBirthday: "",
        dogAgeGroup: "",
        dogActivity: "",
        selectedPromptIndex: 0,
        dogs: [],
        isSocialAuth: false,
      });
    }

    dispatch(logout());
    navigate("/");
  };

  if (deleteStep === "success") {
    return (
      <div className="settings-success-screen">
        <div className="settings-success-content">
          <div className="settings-sad-dog-icon">
            <img src={sadDog} alt="Sad Dog" />
          </div>
          <h2 className="settings-success-title">Account Deleted</h2>
          <p className="settings-success-subtitle">
            Your account has been permanently removed. We're sad to see you go —
            the pack will miss you.
          </p>
          <button className="settings-home-btn" onClick={handleLogout}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page-container">
      <div className="settings-fixed-header">
        <div className="settings-desktop-nav list-desktop-only">
          <div className="settings-desktop-logo" onClick={handleBack}>
            <img src={appIcon} alt="Social Paws" />
            <span>Social Paws</span>
          </div>
        </div>

        <div className="settings-mobile-nav list-mobile-only">
          <MobileTopBar onBack={handleBack} backColor="#fff" logoColor="#fff" />
          <div className="settings-mobile-topbar-wrapper"></div>
        </div>

        <div className="settings-header-content">
          <div className="settings-header-title">
            <SettingsIcon size={26} strokeWidth={2.5} />
            <h2>Settings</h2>
          </div>
          <p className="settings-header-subtitle">
            Manage your account and preferences
          </p>
        </div>
      </div>

      <div className="settings-content-scroll">
        <div className="settings-content-inner">
          <div className="settings-profile-card">
            <div className="settings-profile-info">
              <img
                src={avatarSrc}
                alt="Profile"
                className="settings-profile-avatar"
                onError={(e) => {
                  const fallback = isTeamPaws
                    ? dogPlaceholder
                    : userPlaceholder;
                  if (!e.target.src.includes(fallback)) {
                    e.target.src = fallback;
                  }
                }}
              />
              <div className="settings-profile-text">
                <h3>{displayName}</h3>
                <p title={profile?.user_email}>{profile?.user_email}</p>
                <div className="settings-badge-container">
                  <span
                    className={`settings-team-badge ${
                      !isTeamPaws ? "social-badge" : ""
                    }`}
                  >
                    {isTeamPaws ? "Team Paws" : "Team Social"}
                  </span>
                </div>
              </div>
            </div>
            <div className="settings-profile-action">
              <button
                className="settings-edit-btn"
                onClick={() =>
                  navigate("/select-edit-profile", {
                    state: { from: "/settings-page" },
                  })
                }
              >
                Edit
              </button>
            </div>
          </div>

          <div className="settings-section">
            <span className="settings-section-title">PROFILE & ACCOUNT</span>
            <div className="settings-card">
              <div
                className="settings-row"
                onClick={() =>
                  navigate("/select-edit-profile", {
                    state: { from: "/settings-page" },
                  })
                }
              >
                <div className="settings-row-left">
                  <div className="settings-icon-wrapper light-blue">
                    <User size={22} color="#0093C9" strokeWidth={2} />
                  </div>
                  <div className="settings-row-text">
                    <h4>Edit Profile</h4>
                    <p>Name, bio, photo and dog details</p>
                  </div>
                </div>
                <ChevronRight
                  size={20}
                  color="#A4D9EB"
                  className="settings-chevron"
                />
              </div>
            </div>
          </div>

          <div className="settings-section">
            <span className="settings-section-title">APP PERMISSIONS</span>
            <div className="settings-card">
              <div
                className="settings-row border-bottom"
                onClick={requestLocation}
              >
                <div className="settings-row-left">
                  <div className="settings-icon-wrapper light-blue">
                    <MapPin size={22} color="#0093C9" strokeWidth={2} />
                  </div>
                  <div className="settings-row-text">
                    <h4>Location Access</h4>
                    <p>
                      Enable to sniff around
                      {window.innerWidth <= 768 && <br />}
                      the neighborhood
                    </p>
                  </div>
                </div>
                <div className="settings-row-right">
                  <span
                    className={`settings-status-badge ${
                      locationEnabled ? "enabled" : "disabled"
                    }`}
                  >
                    {locationEnabled ? "Enabled" : "Disabled"}
                  </span>
                  <ChevronRight
                    size={20}
                    color="#A4D9EB"
                    className="settings-chevron"
                  />
                </div>
              </div>

              <div
                className="settings-row"
                onClick={!isIOSSafariBrowser ? requestNotifications : undefined}
                style={
                  isIOSSafariBrowser
                    ? { opacity: 0.5, cursor: "not-allowed" }
                    : {}
                }
              >
                <div className="settings-row-left">
                  <div className="settings-icon-wrapper light-green">
                    <Bell size={22} color="#47D7AC" strokeWidth={2} />
                  </div>
                  <div className="settings-row-text">
                    <h4>Notification Access</h4>
                    <p>
                      {isIOSSafariBrowser ? (
                        "Install app to enable notifications"
                      ) : (
                        <>
                          Enable to stay
                          {window.innerWidth <= 768 && <br />}
                          pupdated
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className="settings-row-right">
                  <span
                    className={`settings-status-badge ${
                      notificationsEnabled ? "enabled" : "disabled"
                    }`}
                  >
                    {notificationsEnabled ? "Enabled" : "Disabled"}
                  </span>
                  <ChevronRight
                    size={20}
                    color="#A4D9EB"
                    className="settings-chevron"
                  />
                </div>
              </div>
            </div>
          </div>

          {VENUE_MANAGEMENT_ENABLED && canManageVenues(profile?.id) && (
            <div className="settings-section">
              <span className="settings-section-title">VENUE MANAGEMENT</span>
              <div className="settings-card">
                <Link className="settings-row" to="/venue-management" style={{ textDecoration: "none", color: "inherit" }}>
                  <div className="settings-row-left">
                    <div className="settings-icon-wrapper light-purple">
                      <MapPin size={22} color="#9333EA" strokeWidth={2} />
                    </div>
                    <div className="settings-row-text">
                      <h4>Manage Venues</h4>
                      <p>Add or deactivate meeting places</p>
                    </div>
                  </div>
                  <ChevronRight size={20} className="settings-chevron" />
                </Link>
              </div>
            </div>
          )}

          <div className="settings-section">
            <span className="settings-section-title">ACCOUNT ACTIONS</span>
            <div className="settings-card">
              <div
                className={`settings-row border-bottom ${isEmailLoading ? "disabled-row" : ""}`}
                onClick={handleEmailSubscriptionToggle}
              >
                <div className="settings-row-left">
                  <div className="settings-icon-wrapper light-purple">
                    <Mail size={22} color="#9333EA" strokeWidth={2} />
                  </div>
                  <div className="settings-row-text">
                    <h4>Email Subscriptions</h4>
                    <p>Receive updates and offers</p>
                  </div>
                </div>
                <div className="settings-row-right">
                  <div
                    className={`settings-toggle ${emailSubscribed ? "active" : ""}`}
                  >
                    <div className="settings-toggle-track"></div>
                    <div className="settings-toggle-thumb">
                      {isEmailLoading && (
                        <Loader2
                          size={12}
                          className="animate-spin"
                          color={emailSubscribed ? "#47d7ac" : "#999"}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="settings-row"
                onClick={() => setShowLogoutModal(true)}
              >
                <div className="settings-row-left">
                  <div className="settings-icon-wrapper light-yellow">
                    <LogOut size={22} color="#D97706" strokeWidth={2} />
                  </div>
                  <div className="settings-row-text">
                    <h4>Log Out</h4>
                    <p>Sign out of your Social Paws account</p>
                  </div>
                </div>
                <ChevronRight
                  size={20}
                  color="#FDE68A"
                  className="settings-chevron"
                />
              </div>
            </div>
          </div>

          <div className="settings-section">
            <span className="settings-section-title">LEGAL</span>
            <div className="settings-card">
              <div
                className="settings-row"
                onClick={() =>
                  window.open(
                    "https://www.socialpawsapp.com/cms/PAWS",
                    "_blank",
                  )
                }
              >
                <div className="settings-row-left">
                  <div className="settings-icon-wrapper light-green">
                    <EyeOff size={22} color="#47D7AC" strokeWidth={2} />
                  </div>
                  <div className="settings-row-text">
                    <h4>Privacy Pawlicy</h4>
                    <p>How we handle your data</p>
                  </div>
                </div>
                <ChevronRight
                  size={20}
                  color="#A4D9EB"
                  className="settings-chevron"
                />
              </div>
            </div>
          </div>

          <div className="settings-section">
            <span className="settings-section-title">DANGER ZONE</span>
            <div className="settings-card">
              <div
                className="settings-row settings-danger-row"
                onClick={() => setDeleteStep("initial")}
              >
                <div className="settings-row-left">
                  <div className="settings-icon-wrapper light-red">
                    <Trash2 size={22} color="#EF4444" strokeWidth={2} />
                  </div>
                  <div className="settings-row-text">
                    <h4 className="text-red">Delete Account</h4>
                    <p>Permanently remove all your data</p>
                  </div>
                </div>
                <ChevronRight
                  size={20}
                  color="#FFA2A2"
                  className="settings-chevron"
                />
              </div>
            </div>
          </div>

          <div className="settings-footer">
            Love the Social Paws v1.0 &middot;{" "}
            {window.innerWidth <= 768 && <br />} Made with love for dog lovers
            :)
          </div>
        </div>
      </div>

      {deleteStep === "initial" && (
        <div className="settings-modal-overlay">
          <div className="settings-modal-card">
            <button
              className="settings-modal-close"
              onClick={() => setDeleteStep("none")}
            >
              <X size={24} />
            </button>
            <div className="settings-modal-icon-container bg-light-red">
              <AlertTriangle size={32} color="#e74c3c" strokeWidth={2} />
            </div>
            <h3 className="settings-modal-title">Delete Account?</h3>
            <p className="settings-modal-desc">This will permanently remove:</p>
            <ul className="settings-modal-list">
              <li>
                <X size={16} color="#e74c3c" strokeWidth={2.5} /> Your profile
                and dog profiles
              </li>
              <li>
                <X size={16} color="#e74c3c" strokeWidth={2.5} /> All your
                messages and connections
              </li>
              <li>
                <X size={16} color="#e74c3c" strokeWidth={2.5} /> Your event
                history and reviews
              </li>
              <li>
                <X size={16} color="#e74c3c" strokeWidth={2.5} /> All account
                data — forever
              </li>
            </ul>
            <p className="settings-modal-warning">
              This action cannot be undone.
            </p>
            <div className="settings-modal-actions">
              <button
                className="settings-btn-primary red"
                onClick={() => setDeleteStep("confirm")}
              >
                I understand, continue
              </button>
              <button
                className="settings-btn-secondary outline-blue"
                onClick={() => setDeleteStep("none")}
              >
                Keep my account
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteStep === "confirm" && (
        <div className="settings-modal-overlay">
          <div className="settings-modal-card">
            <button
              className="settings-modal-close"
              onClick={() => {
                setDeleteStep("none");
                setDeleteInput("");
              }}
            >
              <X size={24} />
            </button>
            <div className="settings-modal-icon-container bg-light-orange">
              <Trash2 size={32} color="#e67e22" strokeWidth={2} />
            </div>
            <h3 className="settings-modal-title">Are you sure?</h3>
            <p className="settings-modal-desc">
              To confirm, type <span className="text-red">DELETE</span> in the
              box below.
            </p>
            <input
              type="text"
              className="settings-delete-input"
              placeholder="Type DELETE here"
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
            />
            <div className="settings-modal-actions mt-lg">
              <button
                className={`settings-btn-primary ${
                  deleteInput === "DELETE" ? "red" : "disabled"
                }`}
                disabled={deleteInput !== "DELETE"}
                onClick={() => setDeleteStep("last_chance")}
              >
                Confirm deletion
              </button>
              <button
                className="settings-btn-secondary outline-blue"
                onClick={() => {
                  setDeleteStep("none");
                  setDeleteInput("");
                }}
              >
                Cancel — keep my account
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteStep === "last_chance" && (
        <div className="settings-modal-overlay">
          <div className="settings-modal-card">
            <button
              className="settings-modal-close"
              onClick={() => {
                if (isDeleting) return;
                setDeleteStep("none");
                setDeleteInput("");
              }}
              disabled={isDeleting}
            >
              <X size={24} />
            </button>
            <div className="settings-modal-icon-container bg-light-red">
              <AlertTriangle size={32} color="#e74c3c" strokeWidth={2} />
            </div>
            <h3 className="settings-modal-title text-red">Last chance</h3>
            <p className="settings-modal-bold-desc">
              You're about to permanently leave the pack.
            </p>
            <p className="settings-modal-desc no-margin">
              Once deleted, your account, all your dog profiles, messages, and
              connections are gone forever — there is no way back.
            </p>
            <div className="settings-modal-actions mt-lg">
              <button
                className="settings-btn-primary red flex-center"
                onClick={executeDeleteProfile}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2
                      size={18}
                      className="animate-spin"
                      style={{ marginRight: "8px" }}
                    />{" "}
                    Deleting...
                  </>
                ) : (
                  "Yes, delete my account"
                )}
              </button>
              <button
                className="settings-btn-primary green"
                onClick={() => {
                  setDeleteStep("none");
                  setDeleteInput("");
                }}
                disabled={isDeleting}
              >
                Actually, I want to stay
              </button>
            </div>
          </div>
        </div>
      )}

      {showLogoutModal && (
        <div
          className="video-modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99999,
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
          }}
        >
          <div
            className="video-modal-card"
            style={{
              background: "#fff",
              padding: "1.5rem",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "350px",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              boxShadow: "0px 10px 30px rgba(0,0,0,0.5)",
              textAlign: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ margin: 0, fontWeight: "700", fontSize: "1.2rem" }}>
                Confirm Logout
              </h3>
              <button
                onClick={() => setShowLogoutModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <X size={24} color="#000" />
              </button>
            </div>
            <p style={{ color: "#555", margin: 0, fontSize: "0.95rem" }}>
              Are you sure you want to log out?
            </p>
            <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
              <button
                onClick={() => setShowLogoutModal(false)}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  background: "#f9f9f9",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#0093c9",
                  color: "#fff",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
